const parsePositiveFiniteInt = (input: string) => {
  const parsed = parseInt(input, 10)
  const valid = isFinite(parsed) && parsed >= 0
  if (!valid) {
    return null
  }
  return parsed
}

const getGradientValues = (input: string | number) => {
  if (!input) {
    return null
  }
  const [val1, val2] = input.toString().split('-')
  if (val1 == null || val2 == null) {
    return null
  }
  const parsed1 = parsePositiveFiniteInt(val1)
  const parsed2 = parsePositiveFiniteInt(val2)
  if (parsed1 == null || parsed2 == null) {
    return null
  }

  return [parsed1, parsed2] as const
}

export const TintingIntensity = {
  full: 'full',
  '85': '85',
  '75': '75',
  '75-35': '75-35',
  '10': '10',
} as const

export type TintingIntensity = `${number}-${number}` | `${number}` | 'full'

export const intensityOpacity: Record<TintingIntensity, number[]> = {
  full: [1],
  '85': [0.9, 0.8],
  '75': [0.8, 0.7],
  '75-35': [0.8, 0.3],
  '10': [0.2, 0.1],
} as const

function isPresetIntensity(input: string): input is TintingIntensity {
  return input in TintingIntensity
}

const getIntensityValues = (input: string) => {
  if (!input) {
    return [1] as const
  }
  const gradient = getGradientValues(input)
  if (gradient) {
    return gradient
  }
  const single = parsePositiveFiniteInt(input)
  if (single) {
    return [single] as const
  }
  return [1] as const
}

const getSingleOpacity = (value: number) => {
  const floor = Math.floor(value / 10)
  return [(floor + 1) / 10, floor / 10] as const
}

const getDoubleOpacity = ([v1, v2]: readonly [number, number]) => {
  const floor1 = Math.floor(v1 / 10)
  const floor2 = Math.floor(v2 / 10)
  return [(floor1 + 1) / 10, floor2 / 10] as const
}
const valuesToOpacity = (
  values: readonly [number] | readonly [number, number]
) => {
  if (values.length === 1) {
    return getSingleOpacity(values[0])
  }
  return getDoubleOpacity(values)
}

const getIntensityOpacity = (input: string) => {
  if (!input) {
    return [1] as const
  }
  if (input === 'full') {
    return [1] as const
  }

  const values = getIntensityValues(input)

  return valuesToOpacity(values)
}

const isValid = (value?: string | null): value is TintingIntensity => {
  if (value == null) {
    return false
  }

  const split = value.split('-')
  if (split == null) {
    return false
  }
  if (split.length > 2) {
    return false
  }
  const val1 = parsePositiveFiniteInt(split[0])
  if (split.length === 1) {
    // For single values, we don't need to check the second value
    return val1 != null
  }
  const val2 = parsePositiveFiniteInt(split[1])
  if (val1 == null || val2 == null) {
    return false
  }
  return true
}

/**
 * Get the gradient code for a given intensity
 *
 * @example
 * getGradientCode('75-35') // 'G7535'
 * getGradientCode('25-00') // 'G2500'
 * getGradientCode('75') // undefined
 */
const getGradientCode = (input: string | number) => {
  if (!input) {
    return undefined
  }
  const gradient = getGradientValues(input)
  if (gradient) {
    const start = `${gradient[0]}`.padStart(2, '0')
    const end = `${gradient[1]}`.padStart(2, '0')
    return `G${start}${end}`
  }
  return undefined
}
export const utils = {
  getIntensityValues,
  parsePositiveFiniteInt,
  getGradientValues,
  getGradientCode,
  getIntensityOpacity,
  isValid,
}
